import './applicantUnemploymentDetailsPage.scss';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import isEmpty from 'lodash/isEmpty';
import toString from 'lodash/toString';
import { ReactNode } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { inputEventHandler } from '../../../components/utils/inputEventHandler';
import useApplicantStore from '../../../store/applicantStore/useApplicantStore';
import useLeadChannelStore from '../../../store/leadChannelStore/useLeadChannelStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const {
    computed: { phoneNumberWithCountryCode },
  } = useLeadChannelStore();
  const { next, previous } = useWorkflowStore();

  const {
    computed: { isCurrentIncomePersisted },
    currentIncomeGrossAnnual,
    isSyncingIncome,
    syncingIncomeFailedMessage,
    setCurrentIncomeGrossAnnual,
    syncIncome,
  } = useApplicantStore();

  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    formState: { errors, isDirty, isValid },
  } = useForm({
    criteriaMode: 'firstError',
    defaultValues: {
      grossAnnualIncomeValue: inputEventHandler.currency.formatter(
        currentIncomeGrossAnnual,
      ),
    },
    delayError: 200,
    mode: 'onBlur',
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const grossAnnualIncomeValidation = {
    setValueAs: (value: string): string =>
      inputEventHandler.currency.parser(value),
    required: t('validation.grossAnnualIncome.required'),
    min: {
      value: 0,
      message: t('validation.grossAnnualIncome.min', { min: '$0' }),
    },
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { grossAnnualIncomeValue } = values;

    if (!grossAnnualIncomeValue) {
      return;
    }

    setCurrentIncomeGrossAnnual(grossAnnualIncomeValue);

    if (isCurrentIncomePersisted && isDirty) {
      const synced = await syncIncome();
      if (synced) {
        navigate(next());
      }

      return;
    }

    navigate(next());
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        className="applicant-unemployment-details-page"
        sx={theme.pageLayout}
      >
        <Header
          progress={useWorkflowStore.getState().computed.progress}
          showDrawerToggle
          onBackClick={previousStep}
        />

        <main>
          <form
            data-testid={locator.pages.applicantUnemploymentDetails.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1
                data-testid={locator.pages.applicantUnemploymentDetails.form.h1}
              >
                {t('ApplicantUnemploymentDetailsPage.title')}
              </h1>
              <h2
                data-testid={locator.pages.applicantUnemploymentDetails.form.h2}
              >
                {t('ApplicantUnemploymentDetailsPage.subtitle')}
              </h2>

              {!isEmpty(syncingIncomeFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.applicantUnemploymentDetails.alert.error
                    }
                    icon={false}
                    severity="error"
                  >
                    {syncingIncomeFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div>
                  <TextField
                    {...register(
                      'grossAnnualIncomeValue',
                      grossAnnualIncomeValidation,
                    )}
                    error={!isEmpty(errors.grossAnnualIncomeValue)}
                    label={t(
                      'ApplicantUnemploymentDetailsPage.grossAnnualIncome',
                    )}
                    slotProps={{
                      htmlInput: {
                        'data-testid':
                          locator.pages.applicantUnemploymentDetails.form
                            .grossAnnualIncomeInput,
                        inputMode: 'numeric',
                      },
                    }}
                    type="text"
                    variant="outlined"
                    fullWidth
                    onBlur={inputEventHandler.currency.blur(
                      'grossAnnualIncomeValue',
                      setValue,
                      trigger,
                    )}
                    onChange={inputEventHandler.currency.change(
                      'grossAnnualIncomeValue',
                      setValue,
                    )}
                    onFocus={inputEventHandler.currency.focus(
                      'grossAnnualIncomeValue',
                      setValue,
                    )}
                    onKeyDown={inputEventHandler.number.keydown}
                    onPasteCapture={inputEventHandler.number.pasteCapture}
                  />

                  {!isEmpty(errors.grossAnnualIncomeValue) && (
                    <span
                      className="field-error"
                      data-testid={
                        locator.pages.applicantUnemploymentDetails.form
                          .grossAnnualIncomeInputError
                      }
                    >
                      {toString(errors.grossAnnualIncomeValue.message)}
                    </span>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <Button
                  data-testid={
                    locator.pages.applicantUnemploymentDetails.form.submitButton
                  }
                  disabled={!isValid}
                  loader={isSyncingIncome}
                  fullWidth
                  submit
                >
                  {t('ApplicantUnemploymentDetailsPage.continue')}
                </Button>
              </div>

              <p
                className="form-disclosure"
                data-testid={locator.pages.applicantUnemploymentDetails.form.incomeDisclosure}
              >
                <Trans
                  components={{
                    phone: <a href={`tel:${phoneNumberWithCountryCode}`} />,
                  }}
                  i18nKey="disclosures.applicantIncome"
                  values={{ phoneNumber: phoneNumberWithCountryCode }}
                />
              </p>
            </div>
            <footer
              data-testid={
                locator.pages.applicantUnemploymentDetails.footer.root
              }
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'ApplicantUnemploymentDetailsPage';
